import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, BookOpen, AlertCircle, Loader2, X, Check } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { MathText } from "../MathText";
import { Modal } from "../ui/Modal";
import { ConfirmDialog } from "../ui/ConfirmDialog";

export interface PassageItem {
  id: string;
  title: string | null;
  text: string;
  body?: string;
  scope: string;
  linkedQuestionsCount?: number;
}

interface AdminPassageSelectorProps {
  scope: string;
  selectedPassageId: string | null | undefined;
  onSelectPassage: (passageId: string | null) => void;
  token: string;
}

export function AdminPassageSelector({
  scope,
  selectedPassageId,
  onSelectPassage,
  token,
}: AdminPassageSelectorProps) {
  const [passages, setPassages] = useState<PassageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal states for Create/Edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPassage, setEditingPassage] = useState<PassageItem | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formText, setFormText] = useState("");
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation modal state
  const [confirmDeletePassage, setConfirmDeletePassage] = useState<PassageItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadPassages = useCallback(async () => {
    if (!scope || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.passages.list(token, scope);
      setPassages(res || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load passages");
    } finally {
      setLoading(false);
    }
  }, [scope, token]);

  useEffect(() => {
    loadPassages();
  }, [loadPassages]);

  const handleOpenCreate = () => {
    setEditingPassage(null);
    setFormTitle("");
    setFormText("");
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (passage: PassageItem) => {
    setEditingPassage(passage);
    setFormTitle(passage.title || "");
    setFormText(passage.text || passage.body || "");
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleSavePassage = async () => {
    const text = formText.trim();
    if (!text) {
      setFormError("Passage markdown text cannot be empty.");
      return;
    }
    if (text.length > 10000) {
      setFormError("Passage text exceeds maximum limit of 10,000 characters.");
      return;
    }

    setFormSaving(true);
    setFormError(null);
    try {
      if (editingPassage) {
        const updated = await api.admin.passages.update(token, editingPassage.id, {
          title: formTitle.trim() || undefined,
          text,
        });
        await loadPassages();
        setIsModalOpen(false);
        onSelectPassage(updated.id);
      } else {
        const created = await api.admin.passages.create(token, {
          title: formTitle.trim() || undefined,
          text,
          scope,
        });
        await loadPassages();
        setIsModalOpen(false);
        onSelectPassage(created.id);
      }
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Failed to save passage");
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeletePassage = async () => {
    if (!confirmDeletePassage) return;
    setDeleting(true);
    try {
      await api.admin.passages.delete(token, confirmDeletePassage.id, scope);
      if (selectedPassageId === confirmDeletePassage.id) {
        onSelectPassage(null);
      }
      setConfirmDeletePassage(null);
      await loadPassages();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete passage");
    } finally {
      setDeleting(false);
    }
  };

  const currentPassage = passages.find((p) => p.id === selectedPassageId);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <BookOpen size={14} className="text-primary" />
          Reading Passage
        </label>
        {loading && <Loader2 size={12} className="animate-spin text-muted-foreground" />}
      </div>

      <div className="flex items-center gap-2">
        <select
          value={selectedPassageId || ""}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "__new__") {
              handleOpenCreate();
            } else {
              onSelectPassage(val ? val : null);
            }
          }}
          className="flex-1 rounded-lg border border-border px-3 py-1.5 text-xs bg-background text-foreground"
        >
          <option value="">None (no reading passage)</option>
          {passages.map((p, idx) => (
            <option key={p.id} value={p.id}>
              {p.title || `Passage ${idx + 1}`} ({p.linkedQuestionsCount ?? 0} linked)
            </option>
          ))}
          <option value="__new__">+ New passage</option>
        </select>

        {currentPassage && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleOpenEdit(currentPassage)}
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition"
              title="Edit passage"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => setConfirmDeletePassage(currentPassage)}
              className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition"
              title="Delete passage"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle size={12} /> {error}
        </p>
      )}

      {/* Modal: Create / Edit Passage */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingPassage ? "Edit Passage" : "Create New Reading Passage"}
        size="lg"
      >
        <div className="space-y-4 text-xs">
          {formError && (
            <div className="p-2.5 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-xs flex items-center gap-2">
              <AlertCircle size={14} className="shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <div>
            <label className="font-semibold text-foreground block mb-1">
              Title (optional)
            </label>
            <input
              type="text"
              placeholder="e.g. Passage 1: The Solar System"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              maxLength={255}
              className="w-full rounded-lg border border-border px-3 py-1.5 bg-background text-foreground text-xs"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="font-semibold text-foreground">
                Passage text (markdown with math $...$ and $$...$$)
              </label>
              <span
                className={`text-xs ${
                  formText.length > 10000 ? "text-destructive font-bold" : "text-muted-foreground"
                }`}
              >
                {formText.length} / 10,000 chars
              </span>
            </div>
            <textarea
              rows={8}
              placeholder="Write or paste reading passage in markdown format..."
              value={formText}
              onChange={(e) => setFormText(e.target.value)}
              maxLength={10000}
              className="w-full rounded-lg border border-border p-3 bg-background text-foreground font-mono text-xs leading-relaxed"
            />
          </div>

          {formText.trim() && (
            <div className="border border-border/80 rounded-lg p-3 bg-muted/20 space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Passage Preview
              </span>
              <div className="prose prose-sm dark:prose-invert max-w-none max-h-48 overflow-y-auto admin-scroll-y p-2 bg-background rounded-md border border-border/50">
                <MathText>{formText}</MathText>
              </div>
            </div>
          )}

          <div className="pt-2 flex items-center justify-end gap-2 border-t border-border">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-3 py-1.5 border border-border rounded-lg text-xs font-semibold hover:bg-muted text-foreground transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSavePassage}
              disabled={formSaving || !formText.trim() || formText.length > 10000}
              className="px-3 py-1.5 bg-primary text-on-accent rounded-lg text-xs font-semibold hover:bg-primary/90 transition flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
            >
              {formSaving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {editingPassage ? "Save Changes" : "Create Passage"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog: Delete Passage */}
      <ConfirmDialog
        isOpen={!!confirmDeletePassage}
        onCancel={() => setConfirmDeletePassage(null)}
        onConfirm={handleDeletePassage}
        loading={deleting}
        title="Delete Passage"
        variant="destructive"
        confirmText="Delete & Unlink"
        message={
          <span>
            Deleting this passage will unlink it from{" "}
            <strong className="text-foreground">
              {confirmDeletePassage?.linkedQuestionsCount ?? 0} question(s)
            </strong>{" "}
            in this set. Are you sure you want to proceed?
          </span>
        }
      />
    </div>
  );
}
